import { V2, hoverOnly } from '../../theme/tokens'
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Divider,
  Alert,
  Button,
  Collapse,
  IconButton,
  Stack,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import RefreshButton from '../common/RefreshButton';
import ProductSearchSelect from '../common/ProductSearchSelect';
import EmptyState from '../common/EmptyState';
import Panel from '../common/Panel';
import FilterControls, { RELATION_COLORS, RELATION_LABELS, DEFAULT_FILTERS } from './FilterControls';
import RecommendationCard, { RecommendationCardSkeleton } from './RecommendationCard';
import useRecommendations from './hooks/useRecommendations';
import useOverrides, { OVERRIDE_DECISIONS } from './hooks/useOverrides';

/**
 * Main product-centric recommendations view.
 * Select a product and see all recommendations grouped by relation type.
 */
export default function ProductRecommendationsView({
  initialProductId,
  onOpenOverrideDialog,
  compactMode = false,
}) {
  // Selected product
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedSections, setExpandedSections] = useState({
    complement: true,
    addon: true,
    competitor: true,
  });

  // Data fetching
  const {
    data,
    recommendations,
    groupedRecommendations,
    loading,
    error,
    refetch,
    updateOverride,
  } = useRecommendations(selectedProduct?.product_id, filters);

  const normalizeProduct = useCallback((product) => {
    if (!product) return null;
    const productId = product.product_id || product.productId || product.id || product.sku;
    const productName = product.product_name
      || product.label
      || product.name
      || product.title
      || product.sku
      || (productId ? String(productId) : '');

    return {
      ...product,
      product_id: productId ? String(productId) : product.product_id,
      product_name: productName,
    };
  }, []);

  React.useEffect(() => {
    if (!initialProductId) return;
    setSelectedProduct((prev) => {
      if (prev?.product_id) return prev;
      return normalizeProduct({
        product_id: initialProductId,
        product_name: String(initialProductId),
      });
    });
  }, [initialProductId, normalizeProduct]);

  React.useEffect(() => {
    if (!data?.product) return;
    setSelectedProduct((prev) => {
      const normalized = normalizeProduct(data.product);
      if (!normalized?.product_id) return prev;
      if (!prev) return normalized;
      if (String(prev.product_id || '') !== String(normalized.product_id)) return prev;

      const needsUpdate = (
        (!prev.product_name || prev.product_name === prev.product_id)
        || (!prev.vendor && normalized.vendor)
        || (!prev.product_type && normalized.product_type)
        || (!prev.image_url && normalized.image_url)
        || (!prev.price && normalized.price)
      );

      if (!needsUpdate) return prev;

      return {
        ...prev,
        product_name: prev.product_name && prev.product_name !== prev.product_id
          ? prev.product_name
          : normalized.product_name,
        vendor: prev.vendor || normalized.vendor,
        product_type: prev.product_type || normalized.product_type,
        image_url: prev.image_url || normalized.image_url,
        price: prev.price || normalized.price,
      };
    });
  }, [data?.product, normalizeProduct]);

  // Override mutations
  const { createOverride } = useOverrides();

  // Handle product selection
  const handleProductSelect = useCallback((product) => {
    setSelectedProduct(normalizeProduct(product));
  }, [normalizeProduct]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Handle quick override action
  // overrideData: { source_product_id, target_product_id, decision, relation_type }
  const handleQuickOverride = useCallback(async (overrideData) => {
    const { target_product_id, decision, relation_type } = overrideData;

    // Optimistically update the UI immediately
    const newOverride = decision === 'auto' ? null : { decision };
    updateOverride(target_product_id, relation_type, newOverride);

    // Then persist to backend (no need to refetch - we already updated locally)
    try {
      await createOverride(
        overrideData.source_product_id,
        target_product_id,
        decision,
        { relationType: relation_type }
      );
    } catch (err) {
      console.error('Failed to create override:', err);
      // Revert on error by refetching
      refetch();
    }
  }, [createOverride, updateOverride, refetch]);

  // Wrapper for opening override dialog that includes refetch callback
  const handleOpenOverrideDialog = useCallback((recommendation) => {
    if (onOpenOverrideDialog) {
      // Pass recommendation, source product, and refetch callback
      onOpenOverrideDialog(recommendation, selectedProduct, refetch);
    }
  }, [onOpenOverrideDialog, selectedProduct, refetch]);

  // Toggle section expansion
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  // Helper to check if an item has been decided (approved, excluded, or force-typed)
  const isDecided = (item) => {
    const decision = item.override?.decision;
    return decision === 'approve' || decision === 'exclude' || decision?.startsWith('force_');
  };

  // Apply client-side filtering and sorting
  const applyFiltersAndSort = (items) => {
    if (!items || items.length === 0) return [];

    let result = [...items];

    // Filter out decided items if hideDecided is true (to-do mode)
    if (filters.hideDecided) {
      result = result.filter(item => !isDecided(item));
    }

    // Filter by min score
    if (filters.minScore > 0) {
      result = result.filter(item => (item.score || 0) >= filters.minScore);
    }

    // Sort
    const { sortBy = 'score', sortOrder = 'desc' } = filters;
    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'name':
          aVal = (a.product_name || '').toLowerCase();
          bVal = (b.product_name || '').toLowerCase();
          break;
        case 'vendor':
          aVal = (a.vendor || '').toLowerCase();
          bVal = (b.vendor || '').toLowerCase();
          break;
        case 'type':
          aVal = (a.product_type || '').toLowerCase();
          bVal = (b.product_type || '').toLowerCase();
          break;
        case 'score':
        default:
          aVal = a.score || 0;
          bVal = b.score || 0;
          break;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  };

  // Get filtered recommendations for current tab
  const getFilteredRecommendations = () => {
    const baseItems = activeTab === 'all'
      ? recommendations
      : (groupedRecommendations[activeTab] || []);
    return applyFiltersAndSort(baseItems);
  };

  const filteredRecommendations = getFilteredRecommendations();

  // Apply filters to grouped recommendations for section view
  const filteredGroupedRecommendations = {
    complement: applyFiltersAndSort(groupedRecommendations.complement || []),
    addon: applyFiltersAndSort(groupedRecommendations.addon || []),
    competitor: applyFiltersAndSort(groupedRecommendations.competitor || []),
  };

  // Count by type for tab badges (always calculate from grouped, not from current tab)
  // Count total items (before hideDecided filter) for progress tracking
  const totalItems = recommendations.length;
  const decidedItems = recommendations.filter(isDecided).length;

  // Counts for tab badges (respects hideDecided filter)
  const counts = {
    all: filteredGroupedRecommendations.complement.length +
         filteredGroupedRecommendations.addon.length +
         filteredGroupedRecommendations.competitor.length,
    complement: filteredGroupedRecommendations.complement.length,
    addon: filteredGroupedRecommendations.addon.length,
    competitor: filteredGroupedRecommendations.competitor.length,
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Product Recommendations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a product to view and manage its recommendations
        </Typography>
      </Box>

      {/* Product Search */}
      <Panel sx={{ p: 2, mb: 2 }}>
        <ProductSearchSelect
          value={selectedProduct}
          onChange={handleProductSelect}
          // Platzhalter bleibt: hier wird eine EMPFEHLUNG gesucht, kein
          // Produkt (G15e). Der Name macht das Feld trotzdem ansprechbar.
          ariaLabel="Search products"
          placeholder="Search by product name, SKU, or ID…"
        />
      </Panel>

      {/* Filters */}
      <Box sx={{ mb: 2 }}>
        <FilterControls
          filters={filters}
          onChange={handleFilterChange}
          showRelationTypes={false}
        />
      </Box>

      {/* Loading indicator */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* No product selected */}
      {!selectedProduct && !loading && (
        <Panel sx={{ p: 4, textAlign: 'center' }}>
          {/* Kein Produkt gewählt — es gibt nichts zu filtern, also no-data. */}
          <EmptyState
            reason="no-data"
            message="Select a product above to view its recommendations"
          />
        </Panel>
      )}

      {/* Recommendations */}
      {selectedProduct && (
        <Panel sx={{ overflow: 'hidden' }}>
          {/* Selected Product Header */}
          <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {selectedProduct.image_url && (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                  }}
                >
                  <img
                    src={selectedProduct.image_url}
                    alt={selectedProduct.product_name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {selectedProduct.product_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedProduct.vendor} • {selectedProduct.product_id}
                </Typography>
              </Box>
              {/* 🔴 W13 (2026-10-07): `IconButton` von Hand mit `disabled`,
                  aber ohne Ladezustand — und im Aufbau standen darunter schon
                  Skelettkarten. `firstLoad` haelt den Knopf dort still
                  (Anti-Pattern 11).
                  🔴 R16 N1: der Diskriminator ist `data === null` („noch nie
                  geladen", der Hook startet damit), nicht
                  `!recommendations.length`. Ein Produkt ohne Empfehlungen ist
                  berechtigt leer — dort sperrte der Knopf beim Neulauf, statt
                  zu drehen. */}
              <RefreshButton
                ariaLabel="Refresh recommendations"
                loading={loading}
                firstLoad={loading && !data}
                onClick={refetch}
              />
            </Box>
          </Box>

          <Divider />

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, value) => {
              setActiveTab(value);
            }}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              value="all"
              label={
                <Badge badgeContent={counts.all} color="primary" max={999}>
                  <Box sx={{ pr: 2 }}>All</Box>
                </Badge>
              }
            />
            <Tab
              value="complement"
              label={
                <Badge badgeContent={counts.complement} color="success" max={999}>
                  <Box sx={{ pr: 2 }}>Complements</Box>
                </Badge>
              }
            />
            <Tab
              value="addon"
              label={
                <Badge badgeContent={counts.addon} color="info" max={999}>
                  <Box sx={{ pr: 2 }}>Add-ons</Box>
                </Badge>
              }
            />
            <Tab
              value="competitor"
              label={
                <Badge badgeContent={counts.competitor} color="error" max={999}>
                  <Box sx={{ pr: 2 }}>Competitors</Box>
                </Badge>
              }
            />
          </Tabs>

          {/* Content */}
          <Box sx={{ p: 2 }} key={`content-${activeTab}`}>
            {/* View mode indicator with progress */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {activeTab === 'all' ? 'All types' : `${activeTab} only`}
                {' • '}
                {filteredRecommendations.length} item{filteredRecommendations.length !== 1 ? 's' : ''}
                {filters.hideDecided && ' (to-do mode)'}
              </Typography>
              {totalItems > 0 && (
                <Chip
                  size="small"
                  label={`${decidedItems}/${totalItems} decided`}
                  color={decidedItems === totalItems ? 'success' : 'default'}
                  variant={decidedItems === totalItems ? 'filled' : 'outlined'}
                  sx={{ height: 20, fontSize: 11 }}
                />
              )}
            </Box>

            {loading && !recommendations.length && (
              <Stack spacing={2}>
                {[1, 2, 3].map(i => (
                  <RecommendationCardSkeleton key={i} compact={compactMode} />
                ))}
              </Stack>
            )}

            {!loading && filteredRecommendations.length === 0 && (
              // Filter und Reiter können die Liste leeren; die UNGEFILTERTE
              // Menge entscheidet, ob es ein Nichttreffer oder wirklich
              // nichts ist (Muster wie ProductsTable/POOverview).
              // 🔴 Ein gescheiterter Abruf ist WEDER Trefferlos NOCH leer —
              //    er steht VOR den beiden (Regel 22 Nr. 3).
              <EmptyState
                reason={error ? 'error' : recommendations.length ? 'no-match' : 'no-data'}
                message={
                  error
                    ? 'Could not load recommendations for this product.'
                    : `No recommendations found${activeTab !== 'all' ? ` of type "${activeTab}"` : ''} for this product`
                }
                action={
                  error ? (
                    <Button size="small" variant="outlined" onClick={() => refetch()}>Try again</Button>
                  ) : undefined
                }
              />
            )}

            {/* Grouped view for 'all' tab */}
            {filteredRecommendations.length > 0 && activeTab === 'all' && (
              <Stack spacing={3}>
                {['complement', 'addon', 'competitor'].map((type) => {
                  const items = filteredGroupedRecommendations[type] || [];
                  if (items.length === 0) return null;

                  return (
                    <RecommendationSection
                      key={type}
                      type={type}
                      items={items}
                      expanded={expandedSections[type]}
                      onToggle={() => toggleSection(type)}
                      sourceProductId={selectedProduct.product_id}
                      onOverride={handleQuickOverride}
                      onOpenOverrideDialog={handleOpenOverrideDialog}
                      compactMode={compactMode}
                    />
                  );
                })}
              </Stack>
            )}

            {/* Flat view for specific type tabs */}
            {filteredRecommendations.length > 0 && activeTab !== 'all' && (
              <Stack spacing={compactMode ? 0 : 2}>
                {filteredRecommendations.map((rec) => (
                  <RecommendationCard
                    key={`${rec.target_product_id}-${rec.relation_type}`}
                    recommendation={rec}
                    sourceProductId={selectedProduct.product_id}
                    onOverride={handleQuickOverride}
                    onOpenOverrideDialog={handleOpenOverrideDialog}
                    compact={compactMode}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Panel>
      )}
    </Box>
  );
}

/**
 * Collapsible section for a relation type.
 */
function RecommendationSection({
  type,
  items,
  expanded,
  onToggle,
  sourceProductId,
  onOverride,
  onOpenOverrideDialog,
  compactMode,
}) {
  const color = RELATION_COLORS[type] || V2.ink45;
  const label = RELATION_LABELS[type] || type;

  // Count overrides
  const overrideCount = items.filter(i => i.override).length;
  const excludedCount = items.filter(
    i => i.override?.decision === OVERRIDE_DECISIONS.EXCLUDE
  ).length;

  return (
    <Box>
      {/* Section Header */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          borderRadius: 1,
          cursor: 'pointer',
          bgcolor: `${color}08`,
          border: `1px solid ${color}30`,
          ...hoverOnly({
            bgcolor: `${color}15`,
          }),
        }}
      >
        <Box
          sx={{
            width: 4,
            height: 24,
            borderRadius: 2,
            bgcolor: color,
          }}
        />
        <Typography variant="subtitle2" fontWeight={600} sx={{ color }}>
          {label}
        </Typography>
        <Chip
          label={items.length}
          size="small"
          sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: `${color}20`,
            color,
          }}
        />
        {overrideCount > 0 && (
          <Chip
            label={`${overrideCount} override${overrideCount > 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
        {excludedCount > 0 && (
          <Chip
            label={`${excludedCount} excluded`}
            size="small"
            color="default"
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton size="small"
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label} section`}>
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
        </IconButton>
      </Box>

      {/* Section Content */}
      <Collapse in={expanded}>
        <Stack spacing={compactMode ? 0 : 1.5} sx={{ mt: 1.5, pl: 1 }}>
          {items.map((rec) => (
            <RecommendationCard
              key={`${rec.target_product_id}-${rec.relation_type}`}
              recommendation={rec}
              sourceProductId={sourceProductId}
              onOverride={onOverride}
              onOpenOverrideDialog={onOpenOverrideDialog}
              compact={compactMode}
            />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}
